import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, ChevronRight, ChevronLeft, Rocket, FlaskConical, Plus, Trash2, Users, Wand2 } from "lucide-react";
import SimulationDialog from "@/components/SimulationDialog";
import { CampaignContactImport } from "@/components/CampaignContactImport";

const STEPS = ["Audience", "Sequence", "Safety", "Review"];

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;

const DEFAULT_SEQUENCE = [
  { step: 1, channel: "linkedin", action: "connect", delay_days: 0,
    template: "Hi {first_name}, I came across {company} and the work you do in logistics. We at Europacific route Asia/overseas cargo into Central & Eastern Europe via the North Adriatic gateway (Ports of Koper & Rijeka) — would be glad to connect." },
  { step: 2, channel: "linkedin", action: "message", delay_days: 3,
    template: "Thanks for connecting, {first_name}! A lot of shippers into CEE are rethinking the Northern-port routing — happy to share what we're seeing on Koper/Rijeka transit times and inland cost. Are you moving any volumes into the region?" },
  { step: 3, channel: "linkedin", action: "follow_page", delay_days: 6,
    template: "By the way {first_name} — we post North Adriatic routing tips, rate trends and customs updates on our company page. Would be great to have you follow Europacific here: [paste your Europacific LinkedIn page link]" },
  { step: 4, channel: "email", action: "follow_up", delay_days: 9,
    template: "Subject: {company} — routing into CEE via Koper\n\nHi {first_name},\n\nFollowing up — many companies shipping into Central & Eastern Europe are cutting transit time and inland trucking cost by routing through Koper/Rijeka instead of the Northern ports. Would it be worth comparing this against your current lanes?\n\nBest,\nRok" },
  { step: 5, channel: "email", action: "value_proposition", delay_days: 18,
    template: "Subject: How companies cut storage & customs cost near Koper\n\nHi {first_name},\n\nOne advantage of a bonded warehouse is that imported goods stay under customs supervision until you need them — deferring duty/VAT and freeing up cash flow.\n\nRight at the North Adriatic gateway, Europacific offers:\n• Bonded + non-bonded storage near the Port of Koper\n• Customs and non-customs cargo handling\n• Flexible storage periods\n• Multimodal onward transport into CEE\n\nIf it's useful, I'd be glad to arrange a short call or a warehouse visit to see whether this could benefit {company}.\n\nBest regards,\nRok Kobal\nEuropacific" },
];

export default function CampaignBuilderPage() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState({
    name: "",
    description: "",
    target_segment: "Heads of Operations — EU Freight Forwarders",
    icp_filters: { industries: "Freight Forwarding, Warehousing, Customs", company_size: "50-500" },
    icp: {
      company_description: "Europacific routes Asia/overseas cargo into Central & Eastern Europe via the North Adriatic gateway (Ports of Koper & Rijeka), with bonded warehousing and multimodal onward transport.",
      industries: "Freight Forwarding, Warehousing, Manufacturing, Automotive, Retail Import",
      geographies: "Slovenia, Austria, Hungary, Czechia, Slovakia, Croatia, Serbia",
      decision_maker_titles: "Head of Logistics, Supply Chain Manager, Operations Director, Import Manager",
      strategic_target_accounts: "",
      anti_icp: "",
      company_size: "50-500",
    },
    sequence_steps: DEFAULT_SEQUENCE.map((s) => ({ ...s, _uid: uid() })),
    safety_settings: { daily_invite_limit: 25, min_gap_seconds: 45, randomize_pct: 20 },
    csv_contacts: null,
    audience_source: null,   // null = AI ICP sourcing | "segment"
    audience_segment: "",    // forwarder | network_partner | shipper | source:url_import | all
    import_network: null,    // e.g. "SEAJET" — pull a whole partner network from the directory
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [networks, setNetworks] = useState(null);
  const [netLoading, setNetLoading] = useState(false);
  const [brief, setBrief] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [hasDrafted, setHasDrafted] = useState(false);
  const [refineText, setRefineText] = useState("");
  const [refining, setRefining] = useState(false);

  const applyDraft = (data) => {
    setForm((f) => {
      const mergedIcp = { ...f.icp };
      Object.entries(data.icp || {}).forEach(([k, v]) => { if (v) mergedIcp[k] = v; });
      const steps = (data.sequence_steps?.length ? data.sequence_steps : f.sequence_steps)
        .map((s) => ({ ...s, _uid: uid() }));
      return {
        ...f,
        name: data.name || f.name,
        description: data.description || f.description,
        target_segment: data.target_segment || f.target_segment,
        icp: mergedIcp,
        sequence_steps: steps,
      };
    });
  };

  const draftWithAI = async () => {
    if (brief.trim().length < 10) { toast.error("Tell the AI your situation and goals first"); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/campaigns/draft-from-brief", { brief }, { timeout: 90000 });
      applyDraft(data);
      setHasDrafted(true);
      toast.success("Campaign drafted with AI — review the ICP and each step, then launch");
      setStepIdx(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed — please try again");
    }
    setDrafting(false);
  };

  const refineWithAI = async () => {
    if (refineText.trim().length < 3) { toast.error("Tell the AI what to tweak (e.g. 'make it warmer')"); return; }
    setRefining(true);
    try {
      const draft = {
        name: form.name, description: form.description, target_segment: form.target_segment,
        icp: form.icp,
        sequence_steps: form.sequence_steps.map((s) => ({
          step: s.step, channel: s.channel, action: s.action, delay_days: s.delay_days, template: s.template,
        })),
      };
      const { data } = await api.post("/campaigns/refine-draft", { draft, instruction: refineText }, { timeout: 90000 });
      applyDraft(data);
      toast.success("Draft refined — review the updated steps");
      setRefineText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI refine failed — please try again");
    }
    setRefining(false);
  };

  const loadNetworks = async () => {
    setNetLoading(true);
    try {
      const { data } = await api.get("/campaigns/directory-networks");
      setNetworks(data.networks || []);
      if (!(data.networks || []).length) toast.info("No networks found — connect your partner directory in Settings.");
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      toast.error(err === "directory_not_configured"
        ? "Connect your partner directory in Settings first."
        : (err || "Could not load networks"));
      setNetworks([]);
    }
    setNetLoading(false);
  };

  const setIcp = (k, v) => setForm((f) => ({ ...f, icp: { ...f.icp, [k]: v } }));

  const updateStep = (i, k, v) => {
    const next = [...form.sequence_steps];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, sequence_steps: next });
  };

  const addStep = () => {
    const next = [...form.sequence_steps,
      { step: form.sequence_steps.length + 1, channel: "email", action: "follow_up", delay_days: 7, template: "", _uid: uid() }];
    setForm({ ...form, sequence_steps: next });
  };

  const removeStep = (i) => {
    const next = form.sequence_steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, step: idx + 1 }));
    setForm({ ...form, sequence_steps: next });
  };

  const generateWithAI = async (i) => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/agents/run", {
        agent_type: "personalization",
        input: {
          prospect: { full_name: "Sample Prospect", title: "VP Operations", company: "Sample Logistics Co", signals: ["fleet_expansion"] },
          channel: form.sequence_steps[i].channel,
          step: form.sequence_steps[i].action,
          tone: "consultative",
        },
      });
      updateStep(i, "template", data.result?.message || "");
      toast.success("AI draft generated");
    } catch (e) { toast.error("AI generation failed"); }
    setAiBusy(false);
  };

  const launch = async () => {
    if (!form.name.trim()) { toast.error("Campaign name required"); return; }
    const splitList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
    const icp = {
      company_description: form.icp.company_description,
      industries: splitList(form.icp.industries),
      geographies: splitList(form.icp.geographies),
      decision_maker_titles: splitList(form.icp.decision_maker_titles),
      strategic_target_accounts: splitList(form.icp.strategic_target_accounts),
      anti_icp: splitList(form.icp.anti_icp),
      company_size: form.icp.company_size,
    };
    let created;
    try {
      const res = await api.post("/campaigns", { ...form, icp });
      created = res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create campaign");
      return;
    }
    try {
      if (form.csv_contacts?.length) {
        await api.post(`/campaigns/${created.id}/import-contacts`, { contacts: form.csv_contacts });
      }
      if (form.import_network) {
        toast.info(`Importing the ${form.import_network} network — this can take a moment…`);
        await api.post(`/campaigns/${created.id}/import-network`, { network: form.import_network });
      }
      await api.post(`/campaigns/${created.id}/launch`);
      toast.success(
        form.csv_contacts?.length
          ? `Campaign launched — ${form.csv_contacts.length} imported contacts enrolled`
          : form.import_network
            ? `Campaign launched — enrolling the ${form.import_network} network now`
            : form.audience_source === "segment"
              ? "Campaign launched — enrolling your selected prospects now"
              : "Campaign launched — sourcing its audience now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Campaign created, but the import/launch step failed — opening it so you can retry.");
    }
    navigate(`/app/campaigns/${created.id}`);
  };

  return (
    <DashboardLayout eyebrow="New Campaign" title="Campaign builder">
      {/* Draft with AI */}
      <div className="data-card max-w-3xl mb-6 border-signal-500/40 bg-signal-500/5" data-testid="ai-brief-panel">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-signal-500" />
          <span className="text-[0.65rem] tracking-widest uppercase text-signal-500 font-semibold">Draft with AI</span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Describe your situation and goal in plain language — the AI fills in the campaign name, ICP,
          and a full multi-step sequence with ready-to-send templates. No copy/paste from ChatGPT needed.
        </p>
        <Textarea data-testid="ai-brief-input" rows={4} value={brief} onChange={(e) => setBrief(e.target.value)}
          placeholder="e.g. We just joined the Seajet network. Goal: an intro campaign to activate new partners, teach them about the CEE region, and show that Slovenia isn't just a destination but a transit country serving Hungary, Austria, the Balkans and Slovakia via Koper/Rijeka." />
        <div className="flex justify-end mt-3">
          <Button onClick={draftWithAI} disabled={drafting} data-testid="ai-brief-generate-btn"
            className="bg-signal-500 hover:bg-signal-400 rounded-sm">
            <Sparkles className="w-4 h-4 mr-2" />{drafting ? "Drafting your campaign…" : "Generate campaign with AI"}
          </Button>
        </div>
        {hasDrafted && (
          <div className="mt-4 pt-4 border-t border-signal-500/20" data-testid="ai-refine-panel">
            <div className="flex items-center gap-2 mb-2">
              <Wand2 className="w-4 h-4 text-signal-500" />
              <span className="text-[0.65rem] tracking-widest uppercase text-signal-500 font-semibold">Refine with AI</span>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              Tweak the draft you just generated — adjust tone or length without starting over. Your edits below are kept.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {["Make the tone warmer", "Make it more direct", "Shorten to 3 steps", "Add a bit more personalization", "Translate to Slovenian"].map((c) => (
                <button key={c} type="button" data-testid={`ai-refine-chip-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setRefineText(c)}
                  className="text-[0.7rem] px-2 py-1 rounded-sm border border-signal-500/30 text-signal-500 hover:bg-signal-500/10 transition-colors">
                  {c}
                </button>
              ))}
            </div>
            <div className="flex gap-2 items-start">
              <Textarea data-testid="ai-refine-input" rows={2} value={refineText} onChange={(e) => setRefineText(e.target.value)}
                placeholder="e.g. Make step 2 shorter and reference the Koper–Budapest lane." className="flex-1" />
              <Button onClick={refineWithAI} disabled={refining} data-testid="ai-refine-btn"
                variant="outline" className="rounded-sm border-signal-500/40 text-signal-500 shrink-0">
                <Wand2 className="w-4 h-4 mr-2" />{refining ? "Refining…" : "Refine"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-3 mb-8 overflow-x-auto">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-3" data-testid={`builder-step-${i}`}>
            <button
              onClick={() => setStepIdx(i)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border transition-colors ${
                i === stepIdx ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="font-mono text-xs">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm">{s}</span>
            </button>
            {i < STEPS.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
          </div>
        ))}
      </div>

      <div className="data-card max-w-3xl space-y-6">
        {stepIdx === 0 && (
          <>
            <div>
              <Label className="text-xs">Campaign name</Label>
              <Input data-testid="builder-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Q1 Freight Forwarders Outreach" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea data-testid="builder-desc-input" rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What's the goal of this campaign?" />
            </div>
            <div>
              <Label className="text-xs">Target segment</Label>
              <Input data-testid="builder-segment-input" value={form.target_segment} onChange={(e) => setForm({ ...form, target_segment: e.target.value })} />
            </div>

            <div className="border border-signal-500/30 bg-signal-500/5 rounded-sm p-4 space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-[0.65rem] tracking-widest uppercase text-signal-500 font-semibold">This campaign's ICP</span>
                <span className="text-[0.6rem] text-muted-foreground">— each campaign sources its own audience from its own ICP</span>
              </div>
              <div>
                <Label className="text-xs">What we offer (for this campaign)</Label>
                <Textarea data-testid="builder-icp-desc" rows={2} value={form.icp.company_description}
                  onChange={(e) => setIcp("company_description", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Target industries</Label>
                  <Input data-testid="builder-icp-industries" value={form.icp.industries}
                    onChange={(e) => setIcp("industries", e.target.value)} placeholder="comma separated" />
                </div>
                <div>
                  <Label className="text-xs">Target geographies</Label>
                  <Input data-testid="builder-icp-geographies" value={form.icp.geographies}
                    onChange={(e) => setIcp("geographies", e.target.value)} placeholder="comma separated" />
                </div>
                <div>
                  <Label className="text-xs">Decision-maker titles</Label>
                  <Input data-testid="builder-icp-titles" value={form.icp.decision_maker_titles}
                    onChange={(e) => setIcp("decision_maker_titles", e.target.value)} placeholder="comma separated" />
                </div>
                <div>
                  <Label className="text-xs">Company size</Label>
                  <Input data-testid="builder-icp-size" value={form.icp.company_size}
                    onChange={(e) => setIcp("company_size", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Strategic target accounts <span className="text-muted-foreground">(optional)</span></Label>
                  <Input data-testid="builder-icp-accounts" value={form.icp.strategic_target_accounts}
                    onChange={(e) => setIcp("strategic_target_accounts", e.target.value)} placeholder="named companies, comma separated" />
                </div>
                <div>
                  <Label className="text-xs">Exclude / anti-ICP <span className="text-muted-foreground">(optional)</span></Label>
                  <Input data-testid="builder-icp-anti" value={form.icp.anti_icp}
                    onChange={(e) => setIcp("anti_icp", e.target.value)} placeholder="never target, comma separated" />
                </div>
              </div>
            </div>

            <div className="border border-border rounded-sm p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-[0.65rem] tracking-widest uppercase text-foreground font-semibold">Or import a contact list (CSV)</span>
                <span className="text-[0.6rem] text-muted-foreground">— target your own list instead of AI sourcing</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Upload your freight-network agents or any contact list. Columns: <span className="font-mono">Company Name, Country, City, Contact Name, Email, Role, Website, Services</span>. When you import a list, this campaign targets ONLY those contacts (no AI net-new sourcing).
              </p>
              <CampaignContactImport onParsed={(contacts) => setForm((f) => ({ ...f, csv_contacts: contacts, import_network: null }))} />
            </div>

            <div className="border border-border rounded-sm p-4 space-y-3" data-testid="builder-network-import">
              <div className="flex items-center gap-2">
                <span className="text-[0.65rem] tracking-widest uppercase text-foreground font-semibold">Or import a partner network</span>
                <span className="text-[0.6rem] text-muted-foreground">— pull a whole logistics network (Seajet, WFN…) as this campaign's audience</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Pulls all of a network's agents from your connected partner directory and targets ONLY them (no AI sourcing). Connect the directory in Settings first.
              </p>
              {networks === null ? (
                <Button type="button" variant="outline" size="sm" onClick={loadNetworks} disabled={netLoading}
                  data-testid="builder-load-networks-btn" className="rounded-sm">
                  <Users className="w-3.5 h-3.5 mr-1.5" />{netLoading ? "Loading networks…" : "Load networks from directory"}
                </Button>
              ) : networks.length === 0 ? (
                <p className="text-[0.7rem] text-muted-foreground">No networks found. Connect your partner directory in Settings.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {networks.map((n) => {
                    const active = form.import_network === n.network;
                    return (
                      <button key={n.network} type="button"
                        data-testid={`builder-import-network-${n.network}`}
                        onClick={() => setForm((f) => active
                          ? { ...f, import_network: null }
                          : { ...f, import_network: n.network, csv_contacts: null, audience_source: null, audience_segment: "" })}
                        className={`flex items-center justify-between rounded-sm border px-3 py-2 text-left transition-colors ${active ? "border-signal-500 bg-signal-500/10" : "border-border hover:border-foreground/40"}`}>
                        <div>
                          <div className="text-sm font-medium">{n.network}</div>
                          <div className="text-[0.65rem] text-muted-foreground">{n.companies} companies · {n.contacts} contacts</div>
                        </div>
                        <Plus className={`w-4 h-4 ${active ? "text-signal-500" : "text-muted-foreground"}`} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border border-border rounded-sm p-4 space-y-3" data-testid="campaign-existing-prospects">
              <div className="flex items-center gap-2">
                <span className="text-[0.65rem] tracking-widest uppercase text-foreground font-semibold">Or target prospects you already have</span>
                <span className="text-[0.6rem] text-muted-foreground">— e.g. contacts you imported from a website / CSV</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Pick a group and this campaign enrolls ONLY those existing prospects (no AI sourcing, no CSV needed).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { id: "", label: "AI sourcing (default)", blurb: "Find net-new leads from this campaign's ICP" },
                  { id: "forwarder", label: "All Forwarder prospects", blurb: "Everyone tagged Forwarder" },
                  { id: "network_partner", label: "All Partnership prospects", blurb: "Everyone tagged Partnership" },
                  { id: "shipper", label: "All Direct customers", blurb: "Everyone tagged Customer / shipper" },
                  { id: "source:url_import", label: "Imported from a website", blurb: "Contacts added via 'From a website'" },
                  { id: "source:campaign_csv", label: "Imported from CSV", blurb: "Contacts added via CSV import" },
                ].map((o) => {
                  const active = (form.audience_source === "segment" && form.audience_segment === o.id) || (!form.audience_source && !form.import_network && !form.csv_contacts?.length && o.id === "");
                  return (
                    <button key={o.id || "ai"} type="button"
                      data-testid={`campaign-audience-${o.id || "ai"}`}
                      onClick={() => setForm((f) => o.id === ""
                        ? { ...f, audience_source: null, audience_segment: "", import_network: null }
                        : { ...f, audience_source: "segment", audience_segment: o.id, import_network: null })}
                      className={`text-left px-3 py-2 rounded-sm border transition-colors ${active ? "border-signal-500 bg-signal-500/10" : "border-border hover:border-foreground/40"}`}>
                      <div className="text-sm font-medium">{o.label}</div>
                      <div className="text-[0.7rem] text-muted-foreground">{o.blurb}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {stepIdx === 1 && (
          <div className="space-y-5">
            {form.sequence_steps.map((s, i) => (
              <div key={s._uid || i} className="border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">Step {s.step}</span>
                    <span className="signal-pill">{s.channel}</span>
                    <span className="signal-pill">{s.action}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => generateWithAI(i)} disabled={aiBusy} data-testid={`builder-ai-gen-${i}`}>
                      <Sparkles className="w-3.5 h-3.5 mr-1" /> {aiBusy ? "Generating..." : "AI draft"}
                    </Button>
                    {form.sequence_steps.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeStep(i)} data-testid={`builder-remove-step-${i}`} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <Label className="text-xs">Delay (days)</Label>
                    <Input type="number" value={s.delay_days} onChange={(e) => updateStep(i, "delay_days", parseInt(e.target.value || "0"))} />
                  </div>
                  <div>
                    <Label className="text-xs">Channel</Label>
                    <Input value={s.channel} onChange={(e) => updateStep(i, "channel", e.target.value)} data-testid={`builder-channel-${i}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Action</Label>
                    <Input value={s.action} onChange={(e) => updateStep(i, "action", e.target.value)} data-testid={`builder-action-${i}`} />
                  </div>
                </div>
                <Label className="text-xs">Template</Label>
                <Textarea data-testid={`builder-template-${i}`} rows={5} value={s.template} onChange={(e) => updateStep(i, "template", e.target.value)} />
              </div>
            ))}
            <Button variant="outline" onClick={addStep} data-testid="builder-add-step-btn" className="w-full rounded-sm">
              <Plus className="w-4 h-4 mr-1.5" /> Add step
            </Button>
          </div>
        )}

        {stepIdx === 2 && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Daily invite limit</Label>
              <Input type="number" data-testid="builder-invite-limit"
                value={form.safety_settings.daily_invite_limit}
                onChange={(e) => setForm({ ...form, safety_settings: { ...form.safety_settings, daily_invite_limit: parseInt(e.target.value || "0") } })} />
            </div>
            <div>
              <Label className="text-xs">Minimum gap between actions (seconds)</Label>
              <Input type="number" value={form.safety_settings.min_gap_seconds}
                onChange={(e) => setForm({ ...form, safety_settings: { ...form.safety_settings, min_gap_seconds: parseInt(e.target.value || "0") } })} />
            </div>
            <div>
              <Label className="text-xs">Randomize pacing (%)</Label>
              <Input type="number" value={form.safety_settings.randomize_pct}
                onChange={(e) => setForm({ ...form, safety_settings: { ...form.safety_settings, randomize_pct: parseInt(e.target.value || "0") } })} />
            </div>
            <p className="text-xs text-muted-foreground">Safety Agent will enforce these limits and throttle if risk score rises.</p>
          </div>
        )}

        {stepIdx === 3 && (
          <div>
            <h3 className="font-display font-semibold text-lg mb-3">Review</h3>
            <dl className="text-sm grid grid-cols-2 gap-y-2 gap-x-6">
              <dt className="text-muted-foreground">Name</dt><dd>{form.name || "—"}</dd>
              <dt className="text-muted-foreground">Segment</dt><dd>{form.target_segment}</dd>
              <dt className="text-muted-foreground">ICP industries</dt><dd>{form.icp.industries || "—"}</dd>
              <dt className="text-muted-foreground">ICP geographies</dt><dd>{form.icp.geographies || "—"}</dd>
              <dt className="text-muted-foreground">Audience</dt><dd>{
                form.import_network ? `${form.import_network} network`
                : form.csv_contacts?.length ? `${form.csv_contacts.length} imported contacts`
                : form.audience_source === "segment" ? "Existing prospects"
                : "AI sourcing"}</dd>
              <dt className="text-muted-foreground">Steps</dt><dd>{form.sequence_steps.length}</dd>
              <dt className="text-muted-foreground">Invite limit / day</dt><dd>{form.safety_settings.daily_invite_limit}</dd>
            </dl>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" disabled={stepIdx === 0} onClick={() => setStepIdx((i) => i - 1)} data-testid="builder-prev-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {stepIdx < STEPS.length - 1 ? (
            <Button onClick={() => setStepIdx((i) => i + 1)} data-testid="builder-next-btn">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <div className="flex gap-2">
              <SimulationDialog
                campaign={form}
                trigger={
                  <Button variant="outline" data-testid="builder-simulate-btn"><FlaskConical className="w-4 h-4 mr-2" />Simulate first</Button>
                }
              />
              <Button onClick={launch} data-testid="builder-launch-btn" className="bg-signal-500 hover:bg-signal-400">
                <Rocket className="w-4 h-4 mr-2" /> Launch campaign
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
